/**
 * vc-objects.js — /video-colorize «Цвет предметов»: a colour for a chosen
 * object, carried through the whole film.
 *
 * Why (question #9753, 18–21.10.2026: a 1943 newsreel, «can the AI paint the
 * uniforms and the tanks by a colour photo?»): DDColor colours by what things
 * usually look like — on that film it left 8 of 12 shots grey, and a khaki
 * tunic came out whatever it guessed. The person KNOWS the colour; what he
 * cannot do is paint 1950 frames. So: he taps the object once (or drags a box),
 * gives its colour (a swatch, HEX, or a colour photo of the same kind of thing),
 * and EdgeTAM's video memory carries the object through the film
 * (EdgeTamVideo in the ro-worker, J&F 0.80 on black-and-white DAVIS frames).
 *
 * Painting is the object-paint engine's (OpColor/OpObject, /photo-recolor):
 * the film's own lightness stays, the colour is the swatch's (tint) or the
 * reference region's L*→a*b* table — the lab's winner for black-and-white by
 * reference (ΔE 2.8 on the same shot, 11.4 from another photo vs DDColor's
 * 14.5). The mask's edge is put on the frame's own edge (MaskEdge.refineEdge).
 *
 * The page owns decode and the render; this module owns the objects, the
 * tracking pass and painting them into a frame:
 *   VcObjects.init(cfg)                 cfg: see init()
 *   VcObjects.applyPreview(ctx, src, ts) after the keyframe's look
 *   VcObjects.applyFrame(img, src, tSec) in the render, per frame (ImageData)
 *   VcObjects.active() / signature() / reset() / busy()
 *
 * «Убрать лишнее» (#9753, 22.10.2026: «ИИ раскрасил и лица солдат в цвет хаки»):
 * a box around soldiers takes their faces with the uniform. In cut mode a tap
 * takes the smallest region under the finger that lies inside the selection and
 * subtracts it (a tap on the same spot again subtracts the next larger one); the
 * selection is its chosen candidate minus every cut, so «Меньше/Больше» keep the
 * cuts and «Вернуть» drops the last one. The track is conditioned on the result.
 * Faces leave a new selection on their own: the face detector /photo-recolor uses
 * finds them on the keyframe, EdgeTAM answers each face's box, and every face the
 * person did not tap on is one cut («Вернуть» brings them all back).
 */
(function (root) {
    'use strict';
    if (root.VcObjects) return;

    var OSL_MIN = 2;          // object score to count a tracked frame (lab cut policy: osl ≥ 2 → J&F 0.84, false frames 156 → 101)
    var MAX_OBJECTS = 6, BOX_START = 8, EDGE_R = 2;
    var CFG = null, T = {}, seg = null, objs = [], act = -1, nextId = 1, pick = false, busy = false, ac = null;
    var kfKey = '', kfFramesSet = false, ref = null, dragging = null, swallow = false, refPick = null, cut = false, cutPick = null;
    var faces = { key: '', p: null, list: [] };
    var $ = function (id) { return document.getElementById(id); };
    function tel(n, p) { try { CFG.tel && CFG.tel(n, p || {}); } catch (_) { } }
    function t(k, map) { var s = String(T[k] || k); for (var m in (map || {})) s = s.split(':' + m).join(map[m]); return s; }
    function show(el, on) { if (el) el.hidden = !on; }

    /* ── masks: frame-size Uint8 ↔ run lengths (a tracked frame is a few KB) ── */
    function rle(mask) {
        var runs = [], cur = 0, n = 0;
        for (var i = 0; i < mask.length; i++) { var v = mask[i] ? 1 : 0; if (v === cur) n++; else { runs.push(n); cur = v; n = 1; } }
        runs.push(n);
        return Uint32Array.from(runs);
    }
    function unrle(runs, n) {
        var m = new Uint8Array(n), p = 0, v = 0;
        for (var i = 0; i < runs.length; i++) { if (v) m.fill(1, p, p + runs[i]); p += runs[i]; v ^= 1; }
        return m;
    }
    // the mask's bounding box in frame pixels (half of the video prompt, see track())
    function boxOf(m, w, h) {
        var x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) if (m[y * w + x]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
    }
    function bitsToMask(bits, n) { var m = new Uint8Array(n); for (var i = 0; i < n; i++) m[i] = (bits[i >> 3] >> (i & 7)) & 1; return m; }
    function areaOf(m) { var a = 0; for (var i = 0; i < m.length; i++) a += m[i]; return a; }
    function overlapOf(a, b) { var n = 0; for (var i = 0; i < a.length; i++) if (a[i] && b[i]) n++; return n; }
    // the selection = its chosen candidate minus every cut
    function withCuts(pr) {
        if (!pr.cuts || !pr.cuts.length) return pr.base;
        var m = Uint8Array.from(pr.base);
        pr.cuts.forEach(function (c) { for (var i = 0; i < m.length; i++) if (c[i]) m[i] = 0; });
        return m;
    }

    /* The object's mask for time `ts` at the tracking size, or null. Before a
       track: the prompt frame itself. */
    function maskAt(o, ts) {
        if (o.track && o.track.times.length) {
            // the render's own rule (colorize-video-pipeline decodeWebCodecs): a
            // target time takes the first decoded frame at or after it
            var tr = o.track, lo = 0, hi = tr.times.length - 1, want = ts - 1e-4;
            if (tr.times[hi] < want) lo = hi;
            else while (lo < hi) { var mid = (lo + hi) >> 1; if (tr.times[mid] >= want) hi = mid; else lo = mid + 1; }
            return tr.runs[lo] ? { m: unrle(tr.runs[lo], tr.w * tr.h), w: tr.w, h: tr.h } : null;
        }
        if (o.prompt && Math.abs(o.prompt.ts - ts) < 0.02) return { m: o.prompt.mask, w: o.prompt.w, h: o.prompt.h };
        return null;
    }

    /* ── painting: the film's lightness, the object's colour ──────────────── */
    function paramsOf(o) {
        var C = root.OpColor;
        if (!o.colour || !C) return null;
        if (o.colour.lut) return { lut: true, P: { lut: o.colour.lut, yScale: 1 } };
        return { lut: false, P: C.tintParams(o.colour.rgb) };
    }
    // mask (mw×mh) → alpha at w×h: nearest scale, then the edge put on the frame's own edge
    function alphaFor(mk, rgba, w, h) {
        var M = new Float32Array(w * h), sx = mk.w / w, sy = mk.h / h, any = false;
        for (var y = 0; y < h; y++) {
            var my = Math.min(mk.h - 1, ((y + 0.5) * sy) | 0) * mk.w;
            for (var x = 0; x < w; x++) if (mk.m[my + Math.min(mk.w - 1, ((x + 0.5) * sx) | 0)]) { M[y * w + x] = 1; any = true; }
        }
        if (!any) return null;
        var r = Math.max(EDGE_R, Math.round(EDGE_R * w / Math.max(1, mk.w)));
        var a = root.MaskEdge ? root.MaskEdge.refineEdge(M, rgba, w, h, r) : null;
        return a || M;
    }
    /* Paint every object present at `ts` into dst (RGBA bytes), lightness from src. */
    function paintInto(dst, src, w, h, ts) {
        var C = root.OpColor, LIN = C && C.LIN, o3 = [0, 0, 0], n = 0;
        if (!C) return 0;
        for (var k = 0; k < objs.length; k++) {
            var o = objs[k], pp = paramsOf(o);
            if (!pp || o.hidden) continue;
            var mk = maskAt(o, ts);
            if (!mk) continue;
            var A = alphaFor(mk, src, w, h);
            if (!A) continue;
            for (var i = 0, p = 0; i < A.length; i++, p += 4) {
                var a = A[i];
                if (a <= 0.004) continue;
                if (a < 1) a = a * a * (3 - 2 * a);                     // smoothstep: the edge stays antialiased, not grey
                var r = LIN[src[p]], g = LIN[src[p + 1]], b = LIN[src[p + 2]];
                if (pp.lut) C.lutPixel(r, g, b, pp.P, o3); else C.tintPixel(r, g, b, pp.P, o3);
                dst[p] = dst[p] + (C.encodeByte(o3[0]) - dst[p]) * a;
                dst[p + 1] = dst[p + 1] + (C.encodeByte(o3[1]) - dst[p + 1]) * a;
                dst[p + 2] = dst[p + 2] + (C.encodeByte(o3[2]) - dst[p + 2]) * a;
            }
            n++;
        }
        return n;
    }
    function srcPixels(src) {
        if (src.data) return src.data;
        return src.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, src.width, src.height).data;
    }
    function applyPreview(ctx, src, ts) {
        if (!objs.length) { drawOverlay(); return; }
        var w = src.width, h = src.height, img = ctx.getImageData(0, 0, w, h);
        if (paintInto(img.data, srcPixels(src), w, h, ts)) ctx.putImageData(img, 0, 0);
        drawOverlay();
    }
    function applyFrame(img, src, tSec) {
        if (!active()) return 0;
        return paintInto(img.data, srcPixels(src), img.width, img.height, tSec);
    }
    function active() { return objs.some(function (o) { return o.colour && !o.hidden && (o.track || o.prompt); }); }
    function signature() {
        return objs.filter(function (o) { return o.colour && !o.hidden; }).map(function (o) {
            return o.id + ':' + (o.colour.lut ? 'ref' + o.colour.rgb.join('.') : o.colour.rgb.join('.')) + ':' + (o.track ? o.track.times.length : 'p') + '@' + (o.prompt ? o.prompt.ts.toFixed(2) : '');
        }).join('|');
    }

    /* ── picking on the keyframe ───────────────────────────────────────────── */
    function kf() { return CFG.getKeyframe ? CFG.getKeyframe() : null; }
    function rgbaOf(c) { return c.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, c.width, c.height).data; }
    function segClient() {
        if (seg || !CFG.seg || !root.EdgeTamClient) return seg;
        seg = root.EdgeTamClient.create({ workerUrl: CFG.seg.workerUrl, engineUrls: CFG.seg.engineUrls, model: CFG.seg.model, video: CFG.seg.video,
            feat: function (n, p) { tel(n, p); } });
        return seg;
    }
    // the keyframe (and the reference photo, letterboxed into its size) as the worker's frames
    async function ensureFrames() {
        var k = kf(); if (!k || !k.canvas) return false;
        var key = k.ts + ':' + k.canvas.width + 'x' + k.canvas.height + (ref ? ':ref' + ref.id : '');
        if (kfFramesSet && kfKey === key) return true;
        var s = segClient(); if (!s) return false;
        var list = [rgbaOf(k.canvas)];
        if (ref) list.push(ref.boxed);
        var ok = await s.setFrames(list, k.canvas.width, k.canvas.height);
        kfFramesSet = ok; kfKey = key;
        return ok;
    }
    function overlayXY(ev) {
        var ov = $('vcObjOverlay'), r = ov.getBoundingClientRect();
        return { x: Math.max(0, Math.min(ov.width - 1, Math.round((ev.clientX - r.left) / r.width * ov.width))),
                 y: Math.max(0, Math.min(ov.height - 1, Math.round((ev.clientY - r.top) / r.height * ov.height))) };
    }
    /* Faces on the keyframe — the shared face-api detector (image-studio/faces.js, the
       one /photo-recolor and /photo-crop use), found once per keyframe in the
       background; a pick waits for them at most 0.8 s. Never throws. */
    function facesFor(k) {
        if (!CFG.facesUrl || !k || !k.canvas) return Promise.resolve([]);
        var key = k.ts + ':' + k.canvas.width + 'x' + k.canvas.height;
        if (faces.key === key && faces.p) return faces.p;
        faces.key = key; faces.list = [];
        faces.p = import(CFG.facesUrl).then(function (m) { return m.detect(k.canvas, k.canvas.width, k.canvas.height); })
            .then(function (b) { b = b || []; if (faces.key === key) faces.list = b; return b; })
            .catch(function () { return []; });
        return faces.p;
    }
    function facesReady(k, ms) {
        var p = facesFor(k);
        return Promise.race([p, new Promise(function (r) { setTimeout(function () { r(faces.list || []); }, ms); })]);
    }
    function faceAt(list, x, y) {
        for (var i = 0; i < list.length; i++) { var f = list[i]; if (x >= f.x && y >= f.y && x <= f.x + f.w && y <= f.y + f.h) return f; }
        return null;
    }
    /* A face as EdgeTAM answers its box, kept to the box and to the selection: the
       detector's box is loose sideways (it takes in the collar beside the cheek), the
       model's head is not — colour rules inside the box could not tell collar from
       cheek on /photo-recolor (22.10.2026). */
    async function faceMask(f, cur, w, h) {
        var b = { x: Math.max(0, Math.round(f.x)), y: Math.max(0, Math.round(f.y)), w: Math.round(f.w), h: Math.round(f.h) };
        var r = await seg.pick({ index: 0, points: [{ x: Math.round(f.x + f.w / 2), y: Math.round(f.y + f.h / 2), label: 1 }], box: b,
                                 segOpts: { mode: 'box', allCands: true, maxFrac: 0.95 } });
        if (!r || !r.mask) return null;
        var m = new Uint8Array(w * h), n = 0;
        for (var y = b.y; y < Math.min(h, b.y + b.h); y++) for (var x = b.x; x < Math.min(w, b.x + b.w); x++) {
            var i = y * w + x;
            if (r.mask[i] && cur[i]) { m[i] = 1; n++; }
        }
        return n >= 16 ? { mask: m, area: n } : null;
    }
    // every face inside a new selection that the tap is not on, as ONE cut
    async function cutFaces(o, k, p) {
        var list = await facesReady(k, 800), w = o.prompt.w, h = o.prompt.h, cur = o.prompt.mask, all = null, n = 0;
        for (var i = 0; i < list.length; i++) {
            var f = list[i], cx = Math.round(f.x + f.w / 2), cy = Math.round(f.y + f.h / 2);
            if (p && faceAt([f], p.x, p.y)) continue;                // the tap is on this face: the face is wanted
            if (cx < 0 || cy < 0 || cx >= w || cy >= h || !cur[cy * w + cx]) continue;
            var fm = await faceMask(f, cur, w, h);
            if (!fm) continue;
            if (!all) all = new Uint8Array(w * h);
            for (var q = 0; q < all.length; q++) if (fm.mask[q]) all[q] = 1;
            n++;
        }
        if (!all) return 0;
        o.prompt.cuts.push(all);
        var m = withCuts(o.prompt);
        if (areaOf(m) < 16) { o.prompt.cuts.pop(); return 0; }
        o.prompt.mask = m;
        return n;
    }
    async function pickAt(p, box) {
        if (busy) return;
        var k = kf(); if (!k || !k.canvas) return;
        busy = true; setMsg(t('obj_selecting'));
        try {
            if (!(await ensureFrames())) { setMsg(t('obj_seg_unavailable'), true); tel('obj_seg_unavailable', {}); return; }
            var r = box ? await seg.pick({ index: 0, points: [], box: box, segOpts: { mode: 'box', allCands: true, maxFrac: 0.95 } })
                        : await seg.pick({ index: 0, points: [{ x: p.x, y: p.y, label: 1 }], segOpts: { mode: 'edge', allCands: true, maxFrac: 0.95 } });
            if (!r || !r.mask) { setMsg(t('obj_nothing_here')); tel('obj_nothing', { box: box ? 1 : 0, reason: r && r.reason }); return; }
            var o = objs[act];
            var cands = (r.cands || []).filter(function (c) { return c && c.mask && c.area > 16; }).sort(function (a, b) { return a.area - b.area; });
            if (!o || o.prompt) {
                if (objs.length >= MAX_OBJECTS) { setMsg(t('obj_too_many', { n: MAX_OBJECTS }), true); return; }
                o = { id: nextId++, colour: null, prompt: null, track: null, hidden: false };
                objs.push(o); act = objs.length - 1;
            }
            o.prompt = { ts: k.ts, base: r.mask, mask: r.mask, cuts: [], tap: box ? null : { x: p.x, y: p.y }, w: k.canvas.width, h: k.canvas.height, cands: cands };
            cutPick = null;
            o.prompt.ci = cands.findIndex(function (c) { return c.area === r.area; });
            o.track = null;                                     // a new selection: the old track no longer belongs to it
            setPick(false);
            var nf = await cutFaces(o, k, box ? null : p);
            if (nf) tel('obj_faces_cut', { n: nf, box: box ? 1 : 0 });
            setMsg(nf ? t('obj_faces_cut', { n: nf }) : (o.colour ? t('obj_track_hint') : t('obj_pick_colour')));
            tel('obj_picked', { via: box ? 'box' : 'tap', frac: +(r.area / (k.canvas.width * k.canvas.height)).toFixed(3), n: objs.length });
            render(); changed();
        } catch (e) {
            setMsg(t('obj_err'), true);
            if (root.__handleError) root.__handleError(e, 'vc_obj_pick', { say: t('obj_err') });
        } finally { busy = false; }
    }
    function stepCand(dir) {
        var o = objs[act]; if (!o || !o.prompt || !o.prompt.cands.length) return;
        var cs = o.prompt.cands, i = o.prompt.ci < 0 ? (dir > 0 ? 0 : cs.length - 1) : o.prompt.ci + dir;
        if (i < 0 || i >= cs.length) { setMsg(t(dir > 0 ? 'obj_no_larger' : 'obj_no_smaller')); return; }
        o.prompt.ci = i; o.prompt.base = cs[i].mask; o.track = null;
        o.prompt.mask = withCuts(o.prompt);
        if (areaOf(o.prompt.mask) < 16) { o.prompt.cuts = []; o.prompt.mask = o.prompt.base; }
        tel('obj_cand_step', { dir: dir, i: i, of: cs.length });
        render(); changed();
    }
    // a point inside the selection: the middle of its box, else the centroid of its pixels
    function insidePoint(m, w, h) {
        var b = boxOf(m, w, h); if (!b) return null;
        var c = { x: Math.round(b.x + b.w / 2), y: Math.round(b.y + b.h / 2) };
        if (m[c.y * w + c.x]) return c;
        var sx = 0, sy = 0, n = 0;
        for (var y = b.y; y < b.y + b.h; y += 2) for (var x = b.x; x < b.x + b.w; x += 2) if (m[y * w + x]) { sx += x; sy += y; n++; }
        if (!n) return null;
        c = { x: Math.round(sx / n), y: Math.round(sy / n) };
        return m[c.y * w + c.x] ? c : null;
    }
    // what a cut tap may take: the regions under the finger that are a part of the
    // selection, not the background beside it; smallest first, one per size (the first
    // of a size wins — the caller puts the model's own answer first)
    function cutCandidates(all, cur) {
        var seen = {};
        return all.filter(function (x) {
            if (!x || !x.mask || x.area < 48 || seen[x.area]) return false;
            seen[x.area] = 1;
            return overlapOf(x.mask, cur) >= 0.5 * x.area;
        }).sort(function (a, b) { return a.area - b.area; });
    }
    /* «Убрать лишнее»: what the finger is on, inside the selection, leaves it */
    async function cutAt(p, box) {
        if (busy) return;
        var o = objs[act], k = kf();
        if (!o || !o.prompt || !k || !k.canvas) { setMsg(t('obj_pick_first')); return; }
        if (Math.abs(o.prompt.ts - k.ts) > 0.02 || o.prompt.w !== k.canvas.width || o.prompt.h !== k.canvas.height) { setMsg(t('obj_cut_frame')); return; }
        busy = true; setMsg(t('obj_selecting'));
        try {
            if (!(await ensureFrames())) { setMsg(t('obj_seg_unavailable'), true); return; }
            var cur = o.prompt.mask, c = null;
            var again = !box && cutPick && cutPick.id === o.id && Math.abs(cutPick.x - p.x) <= 14 && Math.abs(cutPick.y - p.y) <= 14;
            if (again) {
                if (cutPick.i + 1 >= cutPick.cands.length) { setMsg(t('obj_no_larger')); return; }
                c = cutPick.cands[++cutPick.i];
            } else {
                // the object's own point is a «not this» (the /photo-recolor rule, 22.10.2026:
                // alone, a tap on sandbags beside a coat answered sandbags + coat, and the cut
                // took the coat away too); a model that then sees no object is asked plainly
                var tap = [{ x: box ? 0 : p.x, y: box ? 0 : p.y, label: 1 }], own = o.prompt.tap || insidePoint(cur, o.prompt.w, o.prompt.h);
                var negs = own ? [{ x: own.x, y: own.y, label: 0 }] : [];
                var r = box ? await seg.pick({ index: 0, points: negs, box: box, segOpts: { mode: 'box', allCands: true, maxFrac: 0.95 } })
                            : await seg.pick({ index: 0, points: tap.concat(negs), segOpts: { mode: 'edge', allCands: true, maxFrac: 0.95 } });
                if (r && !r.mask && negs.length && (r.reason === 'no-object' || r.reason === 'nothing-at-tap')) {
                    tel('obj_cut_negatives_dropped', { reason: r.reason });
                    r = box ? await seg.pick({ index: 0, points: [], box: box, segOpts: { mode: 'box', allCands: true, maxFrac: 0.95 } })
                            : await seg.pick({ index: 0, points: tap, segOpts: { mode: 'edge', allCands: true, maxFrac: 0.95 } });
                }
                // the model's own answer first (smallest-first took a nose for the face: 113 px
                // of a 2 000-px face on «Army Nurse», 23.10.2026); the same spot again — larger
                var all = (r && r.mask ? [r] : []).concat((r && r.cands) || []);
                // a tap on a face takes the face (a tap on the eye answered the eye: 262 px of ~2 000)
                var onFace = box ? null : faceAt(await facesReady(k, 800), p.x, p.y), fm = onFace ? await faceMask(onFace, cur, o.prompt.w, o.prompt.h) : null;
                if (fm) all.unshift(fm);
                var cs = cutCandidates(all, cur);
                if (!cs.length) { setMsg(t('obj_cut_none')); tel('obj_cut_none', { box: box ? 1 : 0 }); return; }
                var first = Math.max(0, cs.indexOf(fm || r));
                c = cs[first];
                cutPick = box ? null : { id: o.id, x: p.x, y: p.y, cands: cs, i: first };
            }
            var prev = o.prompt.cuts.slice();
            if (again) o.prompt.cuts[o.prompt.cuts.length - 1] = c.mask; else o.prompt.cuts.push(c.mask);
            var m = withCuts(o.prompt);
            if (areaOf(m) < 16) { o.prompt.cuts = prev; setMsg(t('obj_cut_all')); return; }
            o.prompt.mask = m; o.track = null;
            setMsg(t('obj_cut_done'));
            tel('obj_cut', { via: box ? 'box' : 'tap', grow: again ? 1 : 0, cuts: o.prompt.cuts.length,
                             frac: +(c.area / Math.max(1, areaOf(o.prompt.base))).toFixed(3) });
            render(); changed();
        } catch (e) {
            setMsg(t('obj_err'), true);
            if (root.__handleError) root.__handleError(e, 'vc_obj_cut', { say: t('obj_err') });
        } finally { busy = false; }
    }
    function uncut() {
        var o = objs[act]; if (busy || !o || !o.prompt || !o.prompt.cuts.length) return;
        o.prompt.cuts.pop(); o.prompt.mask = withCuts(o.prompt); o.track = null; cutPick = null;
        tel('obj_uncut', { cuts: o.prompt.cuts.length });
        setMsg(o.prompt.cuts.length ? t('obj_cut_done') : t('obj_track_hint'));
        render(); changed();
    }
    // «side by side» hides the overlay the tap lands on: picking shows the frame as one picture
    function frameAsOnePicture() { var side = document.querySelector('[data-vc-view="side"].active'), split = document.querySelector('[data-vc-view="split"]'); if (side && split) split.click(); }
    function markModes() {
        var ov = $('vcObjOverlay');
        if (ov) { ov.classList.toggle('vc-obj-picking', pick || cut); }
        ['vcObjAdd', 'vcObjAddStage'].forEach(function (id) { var b = $(id); if (b) { b.setAttribute('aria-pressed', pick ? 'true' : 'false'); b.classList.toggle('active', pick); } });
        ['vcObjCut', 'vcObjCutStage'].forEach(function (id) { var b = $(id); if (b) { b.setAttribute('aria-pressed', cut ? 'true' : 'false'); b.classList.toggle('active', cut); } });
    }
    function setPick(on) {
        pick = !!on;
        if (pick) { cut = false; frameAsOnePicture(); }
        markModes();
        if (pick) { act = -1; setMsg(t('obj_pick_hint')); facesFor(kf()); }
        drawOverlay();
    }
    function setCut(on) {
        var o = objs[act];
        cut = !!on && !!(o && o.prompt);
        if (cut) { pick = false; cutPick = null; frameAsOnePicture(); setMsg(t('obj_cut_hint')); }
        markModes();
        // the taps land on the frame: from the panel's button it may be above the fold
        var ov = $('vcObjOverlay');
        if (cut && ov && ov.scrollIntoView) ov.scrollIntoView({ block: 'nearest' });
        drawOverlay();
    }
    function onDown(ev) {
        swallow = false;
        if (!pick && !cut) return;
        ev.stopPropagation();
        dragging = { id: ev.pointerId, cx: ev.clientX, cy: ev.clientY, p0: overlayXY(ev), p1: null, on: false };
    }
    function onMove(ev) {
        if (!dragging || ev.pointerId !== dragging.id) return;
        if (!dragging.on) {
            if (Math.hypot(ev.clientX - dragging.cx, ev.clientY - dragging.cy) < BOX_START) return;
            dragging.on = true;
            try { $('vcObjOverlay').setPointerCapture(ev.pointerId); } catch (_) { }
        }
        dragging.p1 = overlayXY(ev);
        drawOverlay(dragging);
    }
    function onUp(ev) {
        var d = dragging; dragging = null;
        if (!d || !d.on || ev.pointerId !== d.id) return;
        swallow = true;
        var x0 = Math.min(d.p0.x, d.p1.x), y0 = Math.min(d.p0.y, d.p1.y);
        var box = { x: x0, y: y0, w: Math.abs(d.p1.x - d.p0.x), h: Math.abs(d.p1.y - d.p0.y) };
        drawOverlay();
        if (box.w >= 6 && box.h >= 6) { if (cut) cutAt(null, box); else pickAt(null, box); }
    }
    function onClick(ev) {
        if (swallow && ev.detail !== 0) { swallow = false; return; }
        if (!pick && !cut) return;
        ev.stopPropagation();
        if (cut) cutAt(overlayXY(ev), null); else pickAt(overlayXY(ev), null);
    }

    /* ── the overlay: outlines of the objects on the shown frame ──────────── */
    function drawOverlay(band) {
        var ov = $('vcObjOverlay'), k = kf();
        if (!ov) return;
        if (!k || !k.canvas) { ov.width = 2; ov.height = 2; return; }
        if (ov.width !== k.canvas.width || ov.height !== k.canvas.height) { ov.width = k.canvas.width; ov.height = k.canvas.height; }
        var cx = ov.getContext('2d'), w = ov.width, h = ov.height;
        cx.clearRect(0, 0, w, h);
        var img = cx.createImageData(w, h), d = img.data, drew = false;
        objs.forEach(function (o, i) {
            if (o.hidden) return;
            var mk = maskAt(o, k.ts);
            if (!mk) return;
            var m = mk.w === w && mk.h === h ? mk.m : null;
            if (!m) return;
            var on = i === act, col = o.colour ? o.colour.rgb : [255, 255, 255];
            for (var y = 1; y < h - 1; y++) for (var x = 1; x < w - 1; x++) {
                var q = y * w + x;
                if (!m[q] || (m[q - 1] && m[q + 1] && m[q - w] && m[q + w])) continue;
                var p = q * 4, dash = ((x + y) >> 2) & 1;
                if (on) { d[p] = d[p + 1] = d[p + 2] = dash ? 255 : 20; } else { d[p] = col[0]; d[p + 1] = col[1]; d[p + 2] = col[2]; }
                d[p + 3] = 255; drew = true;
            }
        });
        if (drew) cx.putImageData(img, 0, 0);
        if (band && band.p1) {
            var lw = Math.max(2, Math.round(w / 300));
            cx.lineWidth = lw; cx.setLineDash([lw * 4, lw * 3]);
            var bx = Math.min(band.p0.x, band.p1.x), by = Math.min(band.p0.y, band.p1.y), bw = Math.abs(band.p1.x - band.p0.x), bh = Math.abs(band.p1.y - band.p0.y);
            cx.strokeStyle = 'rgba(0,0,0,.85)'; cx.strokeRect(bx, by, bw, bh);
            cx.lineDashOffset = lw * 3.5; cx.strokeStyle = '#fff'; cx.strokeRect(bx, by, bw, bh);
            cx.setLineDash([]); cx.lineDashOffset = 0;
        }
    }

    /* ── colour: a swatch, HEX, or a colour photo of the same kind of thing ── */
    function setColour(rgb, src, extra) {
        var o = objs[act];
        if (!o) { setMsg(t('obj_pick_first')); return; }
        o.colour = Object.assign({ rgb: rgb, src: src }, extra || {});
        tel('obj_colour', { src: src });
        setMsg(o.track ? '' : t('obj_track_hint'));
        render(); changed();
    }
    async function loadRef(file) {
        var k = kf(); if (!k || !k.canvas || !file) return;
        try {
            var bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
            var W = k.canvas.width, H = k.canvas.height, s = Math.min(W / bmp.width, H / bmp.height);
            var rw = Math.max(2, Math.round(bmp.width * s)), rh = Math.max(2, Math.round(bmp.height * s));
            var ox = Math.floor((W - rw) / 2), oy = Math.floor((H - rh) / 2);
            var c = $('vcObjRefCanvas'); c.width = W; c.height = H;
            var cx = c.getContext('2d', { willReadFrequently: true });
            cx.fillStyle = '#000'; cx.fillRect(0, 0, W, H); cx.drawImage(bmp, ox, oy, rw, rh);
            if (bmp.close) bmp.close();
            ref = { id: nextId++, boxed: cx.getImageData(0, 0, W, H).data, box: { x: ox, y: oy, w: rw, h: rh } };
            refPick = null;
            kfFramesSet = false;
            show($('vcObjRefBox'), true);
            setMsg(t('obj_ref_hint'));
            tel('obj_ref_loaded', {});
        } catch (e) {
            if (root.__handleError) root.__handleError(e, 'vc_obj_ref', { say: t('obj_err') });
        }
    }
    async function onRefClick(ev) {
        if (!ref || busy) return;
        var o = objs[act]; if (!o) { setMsg(t('obj_pick_first')); return; }
        var c = $('vcObjRefCanvas'), r = c.getBoundingClientRect();
        var p = { x: Math.round((ev.clientX - r.left) / r.width * c.width), y: Math.round((ev.clientY - r.top) / r.height * c.height) };
        busy = true; setMsg(t('obj_selecting'));
        try {
            if (!(await ensureFrames())) { setMsg(t('obj_seg_unavailable'), true); return; }
            // A colour SAMPLE wants the thing under the finger, not «the whole»: the
            // edge rule that suits picking an object in the film took the officer
            // together with the line-up and the hangar behind him on a 1945
            // Kodachrome frame. So the first tap is the smallest answer that is a
            // region at all; a tap on the same spot again steps to the next larger.
            var again = refPick && Math.abs(refPick.x - p.x) <= 14 && Math.abs(refPick.y - p.y) <= 14 && refPick.cands.length;
            var s = null;
            if (again) {
                refPick.i = (refPick.i + 1) % refPick.cands.length;
                s = refPick.cands[refPick.i];
            } else {
                var r0 = await seg.pick({ index: 1, points: [{ x: p.x, y: p.y, label: 1 }], segOpts: { mode: 'edge', allCands: true, maxFrac: 0.95 } });
                var minPx = Math.max(400, 0.003 * c.width * c.height);
                var cs = ((r0 && r0.cands) || []).filter(function (x) { return x && x.mask && x.area >= minPx; }).sort(function (a, b) { return a.area - b.area; });
                if (!cs.length && r0 && r0.mask) cs = [r0];
                refPick = { x: p.x, y: p.y, cands: cs, i: 0 };
                s = cs[0] || null;
            }
            if (!s || !s.mask) { setMsg(t('obj_nothing_here')); return; }
            var R = root.OpObject.referenceOf({ rgba: ref.boxed, w: c.width, h: c.height, mask: s.mask });
            if (!R) { setMsg(t('obj_ref_small')); return; }
            // the reference region, outlined on the photo
            var cx = c.getContext('2d'), img = cx.getImageData(0, 0, c.width, c.height), d = img.data, w = c.width;
            img.data.set(ref.boxed);
            for (var i = w; i < s.mask.length - w; i++) if (s.mask[i] && !(s.mask[i - 1] && s.mask[i + 1] && s.mask[i - w] && s.mask[i + w])) { d[i * 4] = 255; d[i * 4 + 1] = 255; d[i * 4 + 2] = 255; }
            cx.putImageData(img, 0, 0);
            setColour(R.rgb, 'reference', { lut: R.lut });
            if (refPick && refPick.cands.length > 1) setMsg(t('obj_ref_again'));
            tel('obj_ref_region', { px: R.px, small: R.small ? 1 : 0, step: refPick ? refPick.i : 0, of: refPick ? refPick.cands.length : 0 });
        } catch (e) {
            if (root.__handleError) root.__handleError(e, 'vc_obj_ref_pick', { say: t('obj_err') });
        } finally { busy = false; }
    }

    /* ── the tracking pass: every frame once, forward then backward ────────── */
    async function decodeAll(file, w, signal, onP) {
        var X = root.WebCodecsFrameExtractor, frames = [];
        if (!X) throw Object.assign(new Error('no extractor'), { code: 'NO_EXTRACTOR' });
        await X.extract(file, { mode: 'all', maxWidth: w, format: 'jpg', quality: 88, abortSignal: signal,
            onFrame: async function (blob, i, ts) { frames.push({ t: ts, blob: blob }); },
            onProgress: function (p) { onP && onP(p); } });
        frames.sort(function (a, b) { return a.t - b.t; });
        return frames;
    }
    async function frameRgba(f, w, h, cv) {
        var bmp = await createImageBitmap(f.blob);
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(bmp, 0, 0, w, h);
        if (bmp.close) bmp.close();
        return cx.getImageData(0, 0, w, h).data;
    }
    function nearest(frames, ts) {
        var best = 0;
        for (var i = 1; i < frames.length; i++) if (Math.abs(frames[i].t - ts) < Math.abs(frames[best].t - ts)) best = i;
        return best;
    }
    async function track() {
        if (busy) return;
        var list = objs.filter(function (o) { return o.prompt && o.colour && !o.hidden; });
        if (!list.length) { setMsg(t(objs.length ? 'obj_pick_colour' : 'obj_pick_first')); return; }
        var file = CFG.getFile && CFG.getFile(); if (!file) return;
        var s = segClient(); if (!s) { setMsg(t('obj_seg_unavailable'), true); return; }
        busy = true; ac = new AbortController();
        show($('vcObjStop'), true); setBusy(true);
        var t0 = performance.now(), W = list[0].prompt.w, H = list[0].prompt.h, n = 0;
        try {
            progress(0, t('obj_loading_model'));
            var vr = await s.videoReady();
            if (!vr || !vr.ok) { setMsg(t(vr && vr.reason === 'needs-webgpu' ? 'obj_needs_webgpu' : 'obj_seg_unavailable'), true); tel('obj_track_unavailable', { reason: vr && vr.reason }); return; }
            progress(0.02, t('obj_reading'));
            var frames = await decodeAll(file, W, ac.signal, function (p) { progress(0.02 + 0.18 * p, t('obj_reading')); });
            n = frames.length;
            if (!n) throw new Error('no frames');
            var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
            list.forEach(function (o) {
                o.track = { w: W, h: H, times: frames.map(function (f) { return f.t; }), runs: new Array(n), osl: new Float32Array(n), p: nearest(frames, o.prompt.ts) };
                o.track.runs[o.track.p] = rle(o.prompt.mask);
                o.track.osl[o.track.p] = 99;
            });
            var pMin = Math.min.apply(null, list.map(function (o) { return o.track.p; })), pMax = Math.max.apply(null, list.map(function (o) { return o.track.p; }));
            var total = (n - pMin) + pMax, done = 0;
            async function visit(k, dir) {
                if (ac.signal.aborted) throw new DOMException('aborted', 'AbortError');
                await s.videoFrame(await frameRgba(frames[k], W, H, cv), W, H, k);
                var stepping = [];
                for (var i = 0; i < list.length; i++) {
                    var o = list[i];
                    // the selection AND its box: the mask alone loses an even, textureless object
                    // on the 4th frame (so does the transformers reference); together — DAVIS J&F
                    // 0.915 colour / 0.801 b&w (docs/why/primitives/edgetam-video.md)
                    if (dir > 0 && k === o.track.p) await s.videoPrompt({ obj: o.id, n: n, idx: k, mask: o.prompt.mask, box: boxOf(o.prompt.mask, W, H) });
                    else if (dir > 0 ? k > o.track.p : k < o.track.p) stepping.push(o.id);
                }
                if (stepping.length) {
                    var res = await s.videoStep(stepping, k, dir);
                    stepping.forEach(function (id) {
                        var o = list.filter(function (x) { return x.id === id; })[0], r = res[id];
                        if (!o || !r) return;
                        o.track.osl[k] = r.osl;
                        o.track.runs[k] = r.osl >= OSL_MIN && r.area > 0 ? rle(bitsToMask(r.bits, W * H)) : null;
                    });
                }
                done++;
                progress(0.2 + 0.8 * done / Math.max(1, total), t('obj_tracking', { done: done, total: total }));
            }
            for (var k = pMin; k < n; k++) await visit(k, +1);
            for (var j = pMax - 1; j >= 0; j--) await visit(j, -1);
            list.forEach(function (o) {
                var have = 0; for (var q = 0; q < n; q++) if (o.track.runs[q]) have++;
                o.track.cover = have / n;
            });
            var sec = Math.round((performance.now() - t0) / 1000);
            tel('obj_tracked', { objs: list.length, frames: n, sec: sec, cover: list.map(function (o) { return +o.track.cover.toFixed(2); }).join(',') });
            setMsg(t('obj_tracked', { sec: sec }));
        } catch (e) {
            list.forEach(function (o) { o.track = null; });
            if (e && e.name === 'AbortError') { setMsg(t('obj_stopped')); tel('obj_track_stopped', { frames: n }); }
            else if (/^webcodecs-fallback/.test(String(e && e.message))) { setMsg(t('obj_needs_mp4'), true); tel('obj_track_unavailable', { reason: String(e.message).slice(0, 60) }); }
            else {
                setMsg(t('obj_err'), true);
                if (root.__handleError) root.__handleError(e, 'vc_obj_track', { say: t('obj_err') });
            }
        } finally {
            busy = false; ac = null; show($('vcObjStop'), false); setBusy(false); progress(null);
            render(); changed();
        }
    }

    /* ── the panel ─────────────────────────────────────────────────────────── */
    function setMsg(s, warn) { var m = $('vcObjMsg'); if (!m) return; m.textContent = s || ''; m.hidden = !s; m.classList.toggle('vc-obj-warn', !!warn); }
    function setBusy(on) { ['vcObjAdd', 'vcObjAddStage', 'vcObjTrack', 'vcObjHex', 'vcObjRefBtn', 'vcObjCut', 'vcObjCutStage', 'vcObjUncut'].forEach(function (id) { var b = $(id); if (b) b.disabled = !!on; }); CFG.onBusy && CFG.onBusy(!!on); }
    function progress(p, label) {
        var box = $('vcObjProgress'); if (!box) return;
        if (p == null) { box.hidden = true; return; }
        box.hidden = false;
        var f = box.querySelector('.vc-obj-fill'); if (f) f.style.width = Math.round(p * 100) + '%';
        var l = box.querySelector('.vc-obj-plabel'); if (l && label) l.textContent = label;
    }
    function changed() { CFG.onChange && CFG.onChange(); }
    function strip(o) {
        var c = document.createElement('canvas'); c.className = 'vc-obj-strip'; c.width = 300; c.height = 8;
        var cx = c.getContext('2d'), col = o.colour ? 'rgb(' + o.colour.rgb.join(',') + ')' : '#888';
        cx.fillStyle = 'rgba(127,127,127,.25)'; cx.fillRect(0, 0, 300, 8);
        if (o.track) {
            var n = o.track.runs.length; cx.fillStyle = col;
            for (var i = 0; i < n; i++) if (o.track.runs[i]) cx.fillRect(Math.floor(i / n * 300), 0, Math.ceil(300 / n) + 1, 8);
        }
        return c;
    }
    function render() {
        var box = $('vcObjList'); if (!box) return;
        box.innerHTML = '';
        objs.forEach(function (o, i) {
            var row = document.createElement('div'); row.className = 'vc-obj-row' + (i === act ? ' on' : '');
            var sw = document.createElement('span'); sw.className = 'vc-obj-sw';
            sw.style.background = o.colour ? 'rgb(' + o.colour.rgb.join(',') + ')' : 'transparent';
            var nm = document.createElement('button'); nm.type = 'button'; nm.className = 'vc-obj-name';
            nm.textContent = t('obj_n', { n: i + 1 }) + (o.colour && o.colour.lut ? ' · ' + t('obj_from_photo') : '');
            nm.addEventListener('click', function () { act = i; setPick(false); setCut(false); render(); drawOverlay(); });
            var st = document.createElement('span'); st.className = 'vc-obj-st';
            st.textContent = o.track ? t('obj_cover', { pct: Math.round(o.track.cover * 100) }) : (o.colour ? t('obj_not_tracked') : t('obj_no_colour'));
            var del = document.createElement('button'); del.type = 'button'; del.className = 'vc-btn-sm'; del.textContent = '×';
            del.title = t('obj_remove'); del.setAttribute('aria-label', t('obj_remove'));
            del.addEventListener('click', function () {
                if (busy) return;
                if (seg) seg.videoDrop(o.id).catch(function () { });
                objs.splice(i, 1); act = Math.min(act, objs.length - 1);
                if (cut) setCut(false);
                tel('obj_removed', { n: objs.length }); render(); changed();
            });
            row.appendChild(sw); row.appendChild(nm); row.appendChild(st); row.appendChild(del);
            box.appendChild(row);
            if (o.track) box.appendChild(strip(o));
        });
        var o = objs[act], hasCands = !!(o && o.prompt && o.prompt.cands && o.prompt.cands.length > 1);
        show($('vcObjSize'), !!(o && o.prompt));
        show($('vcObjCutStage'), !!(o && o.prompt));
        show($('vcObjSmaller'), hasCands); show($('vcObjLarger'), hasCands);
        show($('vcObjUncut'), !!(o && o.prompt && o.prompt.cuts && o.prompt.cuts.length));
        if (cut && !(o && o.prompt)) setCut(false);
        show($('vcObjColourBox'), !!o);
        var tb = $('vcObjTrack'); if (tb) tb.hidden = !objs.some(function (x) { return x.prompt && x.colour; });
        Array.prototype.forEach.call(document.querySelectorAll('.vc-obj-swatch'), function (b) {
            b.classList.toggle('on', !!(o && o.colour && !o.colour.lut && b.dataset.hex.toLowerCase() === root.OpColor.rgbToHex(o.colour.rgb)));
        });
        drawOverlay();
    }
    function renderSwatches() {
        var box = $('vcObjSwatches'); if (!box) return;
        box.innerHTML = '';
        (CFG.palette || []).forEach(function (hex) {
            var b = document.createElement('button'); b.type = 'button'; b.className = 'vc-obj-swatch';
            b.style.background = hex; b.dataset.hex = hex; b.title = hex; b.setAttribute('aria-label', hex);
            b.addEventListener('click', function () { setColour(root.OpColor.hexToRgb(hex), 'swatch'); });
            box.appendChild(b);
        });
    }

    /* The person's selections — prompt masks and colours, not tracks — as plain
       data that survives a structured clone (IndexedDB): the cashier reloads the
       page after a token purchase (vc-pay.js keeps the work across it). */
    function exportState() {
        return objs.filter(function (o) { return o.prompt; }).map(function (o) {
            return { colour: o.colour, hidden: !!o.hidden,
                     prompt: { ts: o.prompt.ts, w: o.prompt.w, h: o.prompt.h, runs: rle(o.prompt.mask) } };
        });
    }
    function importState(list) {
        if (!Array.isArray(list) || !list.length) return 0;
        objs = list.filter(function (x) { return x && x.prompt && x.prompt.runs; }).slice(0, MAX_OBJECTS).map(function (x) {
            var n = x.prompt.w * x.prompt.h;
            var m = unrle(x.prompt.runs, n);
            return { id: nextId++, colour: x.colour || null, hidden: !!x.hidden, track: null,
                     prompt: { ts: x.prompt.ts, w: x.prompt.w, h: x.prompt.h, base: m, mask: m, cuts: [], tap: null, cands: [], ci: -1 } };
        });
        act = objs.length ? 0 : -1;
        setMsg(objs.length ? t('obj_track_hint') : '');
        tel('obj_restored', { n: objs.length });
        render(); changed();
        return objs.length;
    }
    function reset() {
        if (ac) try { ac.abort(); } catch (_) { }
        if (seg) objs.forEach(function (o) { seg.videoDrop(o.id).catch(function () { }); });
        objs = []; act = -1; ref = null; kfFramesSet = false; kfKey = ''; cutPick = null;
        setPick(false); setCut(false); show($('vcObjRefBox'), false); setMsg('');
        render();
    }

    /**
     * cfg: { seg: {workerUrl, engineUrls, model, video}, t: strings, palette: [hex],
     *        getKeyframe() → {canvas, ts}, getFile() → File, tel(name, props),
     *        onChange() (re-run the live preview), onBusy(bool) }
     */
    function init(cfg) {
        CFG = cfg || {}; T = CFG.t || {};
        var ov = $('vcObjOverlay');
        if (ov) {
            ov.addEventListener('pointerdown', onDown);
            ov.addEventListener('pointermove', onMove);
            ov.addEventListener('pointerup', onUp);
            ov.addEventListener('pointercancel', function () { dragging = null; drawOverlay(); });
            ov.addEventListener('click', onClick);
            // the compare slider below listens on its wrapper: a press that picks is not a drag of the handle
            ['mousedown', 'touchstart'].forEach(function (n) { ov.addEventListener(n, function (e) { if (pick || cut) e.stopPropagation(); }, { passive: true }); });
            ov.addEventListener('touchmove', function (e) { if (dragging && dragging.on) e.preventDefault(); }, { passive: false });
        }
        var on = function (id, ev, fn) { var el = $(id); if (el) el.addEventListener(ev, fn); };
        on('vcObjAdd', 'click', function () { if (!busy) setPick(!pick); });
        // the same switch above the frame: the picture is where the person looks; the panel opens for the colour
        on('vcObjAddStage', 'click', function () { if (busy) return; var g = $('vcObjGroup'); if (g) g.open = true; setPick(!pick); });
        on('vcObjSmaller', 'click', function () { stepCand(-1); });
        on('vcObjLarger', 'click', function () { stepCand(1); });
        on('vcObjCut', 'click', function () { if (!busy) setCut(!cut); });
        on('vcObjCutStage', 'click', function () { if (!busy) setCut(!cut); });
        on('vcObjUncut', 'click', uncut);
        on('vcObjTrack', 'click', function () { track(); });
        on('vcObjStop', 'click', function () { if (ac) ac.abort(); });
        on('vcObjHex', 'change', function () {
            var rgb = root.OpColor.hexToRgb(this.value);
            if (!rgb) { setMsg(t('obj_err_hex'), true); return; }
            setColour(rgb, 'hex');
        });
        on('vcObjRefBtn', 'click', function () { var i = $('vcObjRefInput'); if (i) i.click(); });
        on('vcObjRefInput', 'change', function () { if (this.files && this.files[0]) loadRef(this.files[0]); this.value = ''; });
        on('vcObjRefCanvas', 'click', onRefClick);
        renderSwatches();
        render();
    }

    root.VcObjects = { init: init, applyPreview: applyPreview, applyFrame: applyFrame, active: active, signature: signature, reset: reset,
                       exportState: exportState, importState: importState,
                       busy: function () { return busy; }, redraw: drawOverlay,
                       _rle: rle, _unrle: unrle, _cut: { withCuts: withCuts, candidates: cutCandidates }, _objs: function (a) { if (a) objs = a; return objs; } };
})(typeof window !== 'undefined' ? window : self);
